/**
 * @file useUpload.js
 * @module src/js/hooks/useUpload
 * @description
 * **A hook to upload new items via POST request.**
 * - Utilizes React contexts to manage user state and modal messages.
 * - Returns a function that:
 *   - Sends new data (with or without files) to the backend.
 *   - Handles both FormData (for file uploads) and JSON payloads.
 *   - Updates the local data state upon successful upload.
 *   - Utilizes modal messages to indicate upload status.
 * 
 * @version
 * <p>
 *  2025-12-02: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useFetchFromBackend. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useCallback } from "react";
import { useUser } from "../contexts/UserProvider";
import { useModal } from "../contexts/ModalProvider";
import useFetchFromBackend from "../hooks/useFetchFromBackend";

export default function useUpload({
    setData,
    setUploading,
    setShowUploadForm,
}) {
    const { uid } = useUser();
    const { showModalMessage } = useModal();
    const fetchFromBackend = useFetchFromBackend();

    if (typeof setData !== 'function' ||
        typeof setUploading !== 'function' ||
        typeof setShowUploadForm !== 'function') {
        throw new Error("setData, setUploading, and setShowUploadForm must be functions.");
    }

    const upload = useCallback(async ({
        request,
        body,
        files = [],
        from,
        scrollTo,
    }) => {
        if (!request || !body) {
            throw new Error("Request endpoint and body are required.");
        }
        if (uid === 'Guest') {
            throw new Error("Cannot upload when user is 'Guest'.");
        }

        try {
            setUploading(true);
            showModalMessage({ type: 'info', content: 'Uploading...' });

            let formData = new FormData();
            body.uploader = uid;
            if (files.length > 0) {
                Object.entries(body).forEach(([key, value]) => {
                    formData.append(key, value);
                });

                // Append files in the "file[]" field
                files.forEach(file => {
                    if (file) formData.append("file[]", file);
                });
            }

            const data = await fetchFromBackend({
                request,
                method: "POST",
                body: files.length > 0 ? formData : body,
                uploadFile: files.length > 0,
                requireAuth: true,
                from,
                scrollTo,
            });

            if (data && data.success) {
                const data = await fetchFromBackend({ request });
                setData(data || []);
                setShowUploadForm(false);
                showModalMessage({ type: 'success', content: 'Successfully uploaded!' });

            } else if (data && data.error) {
                console.error("Failed to upload:", data.error);
                showModalMessage({ type: 'error', content: 'Failed to upload. Please try again.' });
            }

        } catch (error) {
            console.error("An error occurred:", error);
            showModalMessage({ type: 'error', content: "An error occurred. Please try again." });

        } finally {
            setUploading(false);
        }

    }, [fetchFromBackend, setData, setUploading, setShowUploadForm, showModalMessage, uid]);
    
    return upload;
}